import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Infinity, ShoppingCart, TrendingUp, Coins } from 'lucide-react';

const perks = [
  {
    icon: Infinity,
    title: 'Raw OCR — 1 credit per page',
    desc: 'POST to /api/v1/ocr to get the full text of any document page. Fast, in-memory, no data stored.',
  },
  {
    icon: ShoppingCart,
    title: 'Field Extraction — 3 credits per page',
    desc: 'POST to /api/v1/ocr/extract with your field list. Get structured JSON with confidence scores for each field.',
  },
  {
    icon: TrendingUp,
    title: 'Credits deducted only on success',
    desc: 'If OCR or extraction fails for any reason, no credits are deducted. You only pay for results you receive.',
  },
];

export default function CreditSystem({ onGetStarted }) {
  const [ref, visible] = useScrollAnimation();

  return (
    <section id='credits' ref={ref} className={cn('py-24 bg-surface reveal', visible && 'revealed')}>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='grid lg:grid-cols-2 gap-16 items-center'>

          {/* Left: visual */}
          <div className={cn('flex justify-center reveal reveal-delay-1', visible && 'revealed')}>
            <div className='relative'>
              {/* Credit wallet card */}
              <div className='bg-gradient-cta rounded-3xl p-8 w-72 shadow-float relative overflow-hidden'>
                {/* Background dot pattern */}
                <div className='absolute inset-0 dot-pattern-hero opacity-40' />
                <div className='relative z-10'>
                  <div className='flex items-center gap-3 mb-6'>
                    <div className='w-10 h-10 rounded-xl bg-primary-foreground/15 flex items-center justify-center'>
                      <Coins className='w-5 h-5 text-primary-foreground' />
                    </div>
                    <div>
                      <p className='text-xs text-primary-foreground/60 font-medium'>Available Credits</p>
                    </div>
                  </div>
                  <p className='font-heading font-bold text-5xl text-primary-foreground mb-1'>10</p>
                  <p className='text-sm text-primary-foreground/60 mb-6'>Bonus credits · No card needed</p>

                  {/* Usage bar */}
                  <div className='space-y-2'>
                    <div className='flex justify-between text-xs text-primary-foreground/70'>
                      <span>Usage so far</span>
                      <span>3 / 10</span>
                    </div>
                    <div className='w-full bg-primary-foreground/15 rounded-full h-2'>
                      <div className='bg-accent h-2 rounded-full' style={{ width: '30%' }} />
                    </div>
                    <p className='text-xs text-primary-foreground/50'>7 credits remaining</p>
                  </div>
                </div>
              </div>

              {/* Floating tag */}
              <div className='absolute -bottom-5 -right-5 bg-card shadow-float border border-border rounded-2xl px-4 py-3'>
                <p className='text-xs text-muted-foreground'>Extract endpoint</p>
                <p className='font-heading font-bold text-xl text-foreground'>
                  3<span className='text-sm font-normal text-muted-foreground'> cr/page</span>
                </p>
              </div>

              {/* Credits coin */}
              <div className='absolute -top-4 -right-4 w-12 h-12 rounded-full bg-credit shadow-[0_4px_20px_hsl(38_92%_50%/0.4)] flex items-center justify-center animate-float-delay'>
                <Coins className='w-5 h-5 text-credit-foreground' />
              </div>
            </div>
          </div>

          {/* Right: copy */}
          <div className='space-y-8'>
            <div>
              <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-credit/10 text-credit border border-credit/20 mb-4'>
                Credit System
              </span>
              <h2 className='font-heading font-bold text-4xl lg:text-5xl text-foreground mb-4'>
                Pay only for what
                <span className='text-gradient-brand'> you process</span>
              </h2>
              <p className='text-muted-foreground text-lg leading-relaxed'>
                1 credit = 1 page of raw OCR. 3 credits = 1 page of structured field extraction.
                Credits are deducted only on successful processing.
              </p>
            </div>

            <div className='space-y-5'>
              {perks.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={p.title} className={cn('flex gap-4 reveal', `reveal-delay-${i + 2}`, visible && 'revealed')}>
                    <div className='w-10 h-10 rounded-xl bg-credit/10 flex items-center justify-center flex-shrink-0'>
                      <Icon className='w-5 h-5 text-credit' />
                    </div>
                    <div>
                      <h3 className='font-semibold text-foreground text-sm mb-1'>{p.title}</h3>
                      <p className='text-muted-foreground text-sm leading-relaxed'>{p.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button variant='default' size='lg' onClick={onGetStarted} className='gap-2 shadow-primary'>
              Get 10 Bonus Credits
              <Coins className='w-4 h-4' />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
